import {
  collection,
  getDocs,
  query,
  where,
  doc,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { Status } from "../constants/status";
import { TableName } from "../constants/tableName";
import { SlotStatus } from "../constants/slotStatus";

export class ReservationsController {
  static getAll({ type, reservationSettingsId, resolve, reject }) {
    let errorMessage =
      "Unable to get all the reservations details.\nPlease contact administrator for assistance.";
    const q = query(
      collection(db, type),
      where("reservationSettingsId", "==", reservationSettingsId)
    );
    getDocs(q)
      .then((data) => {
        console.debug(data);
        resolve(data);
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  }

  static getAllViaReservationDate({
    type,
    reservationSettingsId,
    reservationDate,
    resolve,
    reject,
  }) {
    let errorMessage =
      "Unable to get all today reservations details.\nPlease contact administrator for assistance.";
    const q = query(
      collection(db, type),
      where("reservationSettingsId", "==", reservationSettingsId),
      where("reservationDate", "==", reservationDate)
    );
    getDocs(q)
      .then((data) => {
        console.debug(data);
        resolve(data);
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  }

  static getAllViaTypeAndReservationSettingsId = ({
    type,
    reservationSettingsId,
    resolve,
    reject,
  }) => {
    let errorMessage =
      "Unable to get all the reservations details.\nPlease contact administrator for assistance.";
    const q = query(
      collection(db, type),
      where("reservationSettingsId", "==", reservationSettingsId)
    );
    getDocs(q)
      .then((data) => {
        console.debug(data);
        resolve(data);
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static async get({ type, reservationSettingsId, userId, resolve, reject }) {
    const errorMessage =
      "Unable to get all the reservation\nPlease contact administrator for assistance.";
    try {
      const reservationsQuery = query(
        collection(db, type),
        where("userId", "==", userId),
        where("reservationSettingsId", "==", reservationSettingsId)
      );
      const querySnapshot = await getDocs(reservationsQuery);
      resolve(querySnapshot);
    } catch (error) {
      reject(this.formatError(errorMessage, error).message);
    }
  }

  static async add({ type, parameters, resolve, reject }) {
    const errorMessage =
      "Unable to perform the reservation. Please contact administrator for assistance.";
    try {
      if (await this.onlyAllowOnceSlotPerType(type, parameters))
        return reject(
          this.formatError(
            `You have one ${Status.getStatusValue(
              Status.ACTIVE
            )} ${TableName.getTableNameValue(
              type
            )} reservation.\nYou cannot make another ${TableName.getTableNameValue(
              type
            )} reservation.`,
            new Error(
              `More than one ${Status.getStatusValue(
                Status.ACTIVE
              )} reservation.`
            )
          ).message
        );
      if (await this.checkAvailableSlot(type, parameters)) {
        return reject(
          this.formatError(
            "Insufficient slots available for your reservation.",
            new Error("Insufficient slots.")
          ).message
        );
      }
      await runTransaction(db, async (transaction) => {
        const newReservationRef = doc(collection(db, type));
        transaction.set(newReservationRef, {
          ...parameters,
          status: Status.ACTIVE,
        });
      });
      resolve(
        "Your reservation has been placed successfully. Check MY RESERVATIONS for details."
      );
    } catch (error) {
      reject(this.formatError(errorMessage, error).message);
    }
  }

  static async onlyAllowOnceSlotPerType(type, parameters) {
    return (
      (
        await getDocs(
          query(
            collection(db, type),
            where("userId", "==", parameters.userId),
            where("status", "==", Status.ACTIVE)
          )
        )
      ).docs.length >= 1
    );
  }

  static async checkCanReserve({ type, parameters, resolve, reject }) {
    const errorMessage =
      "Unable to check the reservation. Please contact administrator for assistance.";
    try {
      resolve(await this.onlyAllowOnceSlotPerType(type, parameters));
    } catch (error) {
      reject(this.formatError(errorMessage, error).message);
    }
  }

  static async checkAvailableSlot(type, parameters) {
    const totalPaxReserved = (
      await getDocs(
        query(
          collection(db, type),
          where("reservationDate", "==", parameters.reservationDate),
          where("timeSlot.id", "==", parameters.timeSlot.id),
          where("status", "==", Status.ACTIVE)
        )
      )
    ).docs.reduce((total, doc) => total + (doc.data().pax || 0), 0);
    return parameters.timeSlot.maxPax - totalPaxReserved < parameters.pax;
  }

  static checkEachSlotStatus = ({
    type,
    reservationDate,
    timeSlotsId,
    maxPax,
    slotStatus,
    resolve,
    reject,
  }) => {
    const errorMessage =
      "Unable to get total reservations count.\nPlease contact administrator for assistance.";
    getDocs(
      query(
        collection(db, type),
        where("reservationDate", "==", reservationDate),
        where("timeSlot.id", "==", timeSlotsId),
        where("status", "in", [Status.ACTIVE, Status.COMPLETED])
      )
    )
      .then((data) => {
        const totalPax = data.docs.reduce(
          (total, doc) => total + (doc.data().pax || 0),
          0
        );
        const middlePax = Math.ceil(maxPax / 2);
        resolve(
          totalPax >= maxPax
            ? SlotStatus.FULLYBOOKED
            : totalPax >= middlePax
            ? SlotStatus.LIMITEDQUANTITY
            : slotStatus
        );
      })
      .catch((error) => reject(this.errorHandling({ errorMessage, error })));
  };

  static async update({ type, parameters, resolve, reject }) {
    const errorMessage =
      "Unable to update the reservation.\nPlease contact administrator for assistance.";
    try {
      const reservationRef = doc(db, type, parameters.id);
      await runTransaction(db, async (transaction) => {
        transaction.update(reservationRef, parameters);
      });
      resolve("Selected time slots status is successfully changed.");
    } catch (error) {
      reject(this.formatError(errorMessage, error).message);
    }
  }

  static async cancel({ type, parameters, resolve, reject }) {
    const errorMessage =
      "Unable to cancel the reservation.\nPlease contact administrator for assistance.";
    try {
      const reservationRef = doc(db, type, parameters.id);
      await runTransaction(db, async (transaction) => {
        transaction.update(reservationRef, parameters);
      });
      resolve("Your reservation has been successfully cancelled.");
    } catch (error) {
      reject(this.formatError(errorMessage, error).message);
    }
  }

  static formatError(errorMessage, error) {
    console.error("Error:", error.message);
    let userErrorMessage = errorMessage;
    if (error.code === "permission-denied") {
      userErrorMessage = "You are not authorized for this action.";
    } else if (error.code === "not-found") {
      userErrorMessage = "Requested reservation was not found.";
    }
    return {
      message: userErrorMessage,
      details: error.message,
    };
  }
}
